import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';
import WhatsAppButton from '@/components/WhatsAppButton';
import { Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Shop = () => {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(category || 'all');

  useEffect(() => {
    document.title = 'Shop Mobile Accessories – RSD Accessories';
    fetchCategories();
  }, []);

  useEffect(() => {
    // Scroll to top when category changes
    window.scrollTo(0, 0);
    setSelectedCategory(category || 'all');
    fetchProducts(category);
  }, [category]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async (cat) => {
    setLoading(true);
    try {
      const url = cat ? `${API}/products?category=${cat}` : `${API}/products`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    if (cat === 'all') {
      window.history.pushState({}, '', '/shop');
      fetchProducts(null);
    } else {
      window.history.pushState({}, '', `/shop/${cat}`);
      fetchProducts(cat);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Shop All Products</h1>
          <p className="text-lg text-neutral-600">
            Explore our complete range of premium accessories
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl p-6 shadow-sm sticky top-24">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-5 h-5" />
                <h2 className="font-bold text-lg">Categories</h2>
              </div>
              <div className="space-y-2">
                <button
                  data-testid="category-all"
                  onClick={() => handleCategoryChange('all')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-colors ${
                    selectedCategory === 'all'
                      ? 'bg-[#FF4D00] text-white'
                      : 'hover:bg-neutral-50 text-neutral-700'
                  }`}
                >
                  All Products
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    data-testid={`category-filter-${cat.id}`}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-colors flex justify-between items-center ${
                      selectedCategory === cat.id
                        ? 'bg-[#FF4D00] text-white'
                        : 'hover:bg-neutral-50 text-neutral-700'
                    }`}
                  >
                    <span>{cat.name}</span>
                    <span className="text-sm opacity-75">({cat.count})</span>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF4D00]"></div>
              </div>
            ) : products.length === 0 ? (
              <div data-testid="no-products" className="text-center py-20">
                <h3 className="text-2xl font-bold mb-2">No products found</h3>
                <p className="text-neutral-600">Try selecting a different category</p>
              </div>
            ) : (
              <>
                <div className="mb-4 text-sm text-neutral-600">
                  Showing <span className="font-semibold text-black">{products.length}</span> products
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <WhatsAppButton />
    </div>
  );
};

export default Shop;